import { useEffect, useState } from "react";
import "./TransportLogistics.css";
import DesktopLogistics from "../../assets/images/transport.png";
import MobileLogistics from "../../assets/images/katnavMobSales.png";
import ContactForm from "../../components/ContactUs/ContactUs";
import {
  Truck,
  Package,
  Plane,
  Ship,
  Warehouse,
  BarChart3,
  Users,
  CheckCircle,
  Clock,
  Target,
} from "lucide-react";

const TransportLogistics = () => {
  const [showContact, setShowContact] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [visibleSections, setVisibleSections] = useState(new Set());

  // Scroll to top and trigger animations
  useEffect(() => {
    window.scrollTo(0, 0);

    // Trigger hero animations
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 300);

    // Set up intersection observer for scroll-triggered animations
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.1 }
    );

    // Observe all sections
    const sections = document.querySelectorAll('[id^="transport-logistics-"]');
    sections.forEach((section) => observer.observe(section));

    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, []);

  const transportModes = [
    {
      icon: Truck,
      title: "Road Transportation",
      description:
        "Comprehensive road freight management with route optimization and real-time tracking.",
    },
    {
      icon: Ship,
      title: "Sea Freight",
      description:
        "Complete sea freight solutions for international shipping and container management.",
    },
    {
      icon: Plane,
      title: "Air Freight",
      description:
        "Fast and reliable air cargo services with priority handling and tracking.",
    },
    {
      icon: Warehouse,
      title: "Public Warehousing",
      description:
        "Flexible warehousing solutions with inventory management and distribution services.",
    },
  ];

  const benefits = [
    "Freely definable calculation schemes and production schedules for optimized budget based production planning",
    "Integrated workflows and high operative transparency in the entire organization",
    "An out-of-box solution for furniture companies that can be easily integrated and customized",
    "Customizable cost schemas for more accurate planning of budgets",
    "Improved efficiency both when producing on order or for a specific design",
    "Fast implementation time for best ROI results",
    "Modern and intuitive user interface",
    "Complete functionality for manufacturing of all types of furniture, including upholstery and fittings",
    "Intuitive use, compliant with Dynamics NAV logics",
    "Insights into processes and data for accurate decision making",
  ];

  const competitiveFactors = [
    {
      icon: Target,
      title: "Maximum Capacity Utilization",
      description:
        "Optimize vehicle and warehouse capacity to reduce costs and improve efficiency.",
    },
    {
      icon: Clock,
      title: "Just in Time Delivery",
      description:
        "Precise timing and coordination to meet customer delivery requirements.",
    },
    {
      icon: BarChart3,
      title: "Real-time Order Monitoring",
      description:
        "Live tracking and monitoring of orders throughout the logistics chain.",
    },
    {
      icon: Package,
      title: "Avoid Dead Cargo",
      description:
        "Intelligent planning to minimize empty runs and maximize profitability.",
    },
  ];

  return (
    <div className="transport-logistics-main-container">
      {/* Hero Section */}
      <section className="transport-logistics-hero-wrapper">
        <div className="transport-logistics-hero-background">
          <div className="transport-logistics-floating-element transport-logistics-element-1"></div>
          <div className="transport-logistics-floating-element transport-logistics-element-2"></div>
          <div className="transport-logistics-floating-element transport-logistics-element-3"></div>
          <div className="transport-logistics-circle-decoration-one"></div>
          <div className="transport-logistics-circle-decoration-two"></div>
        </div>

        <div className="transport-logistics-content-width">
          <div className="transport-logistics-center-align">
            <span
              className={`transport-logistics-nav-badge ${
                isVisible ? "transport-logistics-slide-down" : ""
              }`}
            >
              Microsoft Dynamics NAV Solutions
            </span>
            <h1
              className={`transport-logistics-hero-main-title ${
                isVisible ? "transport-logistics-slide-up" : ""
              }`}
            >
              <span className="transport-logistics-gradient-text">
                TRANSPORT & LOGISTICS
              </span>
            </h1>
            <p
              className={`transport-logistics-hero-subtitle ${
                isVisible ? "transport-logistics-fade-in" : ""
              }`}
            >
              Logistics is generally the detailed organization and
              implementation of a complex operation. In a general business
              sense, logistics is the management of the flow of things between
              the point of origin and the point of consumption to meet
              requirements of customers or corporations.
            </p>
            <div
              className={`transport-logistics-hero-buttons ${
                isVisible ? "transport-logistics-bounce-in" : ""
              }`}
            >
              <button
                className="transport-logistics-primary-button"
                onClick={() => (window.location.href = "/products/portfolio")}
              >
                <Truck className="transport-logistics-small-icon" />
                Explore Solutions
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Logistics Overview Section */}
      <section
        id="transport-logistics-overview"
        className={`transport-logistics-overview-area ${
          visibleSections.has("transport-logistics-overview")
            ? "transport-logistics-section-visible"
            : ""
        }`}
      >
        <div className="transport-logistics-content-width">
          <div className="transport-logistics-overview-content">
            <p className="transport-logistics-overview-description transport-logistics-fade-in-up">
              The resources managed in logistics may include tangible goods such
              as materials, equipment, and supplies, as well as food and other
              consumable items. The logistics of physical items usually involves
              the integration of information flow, materials handling,
              production, packaging, inventory, transportation, warehousing, and
              often security.
            </p>
          </div>
        </div>
      </section>

      {/* Microsoft Dynamics NAV Section */}
      <section
        id="transport-logistics-nav"
        className={`transport-logistics-nav-section ${
          visibleSections.has("transport-logistics-nav")
            ? "transport-logistics-section-visible"
            : ""
        }`}
      >
        <div className="transport-logistics-content-width">
          <div className="transport-logistics-nav-layout">
            <div className="transport-logistics-nav-content">
              <h2 className="transport-logistics-nav-heading transport-logistics-slide-left">
                Microsoft Dynamics NAV for Transport & Logistics
              </h2>
              <p className="transport-logistics-nav-description transport-logistics-fade-in-up">
                It is important that when making the item through the Transfer
                Order from one point to another the item must receive the
                condition that is in TRANSIT both in terms of the stock we have
                in stock and the item, for this item we have made exit and is in
                Shipping for your destination to request the status of the
                details and reports made in Shipping.
              </p>
              <p className="transport-logistics-nav-description transport-logistics-fade-in-up">
                Dynamics Navision gives us the opportunity to display the stock
                we have in Shipping In the Shipping Code the warehouse will be
                released from the warehouse in Code Transit After that Passage
                Code to the destination of the item.
              </p>
            </div>
            <div className="transport-logistics-nav-image">
              <img
                src={DesktopLogistics || "/placeholder.svg"}
                alt="Microsoft Dynamics NAV transport and logistics dashboard showing transfer orders and transit status"
                className="transport-logistics-main-image transport-logistics-scale-in"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Competitive Factors Section */}
      <section
        id="transport-logistics-factors"
        className={`transport-logistics-factors-area ${
          visibleSections.has("transport-logistics-factors")
            ? "transport-logistics-section-visible"
            : ""
        }`}
      >
        <div className="transport-logistics-content-width">
          <div className="transport-logistics-center-align">
            <h2 className="transport-logistics-factors-heading transport-logistics-zoom-in">
              Key Competitive Factors
            </h2>
            <p className="transport-logistics-factors-description transport-logistics-fade-in">
              Customers choose transport companies based on a number of
              necessary factors. The transport and logistics sector has
              experienced increased competition, making it essential for
              transport companies to adapt the internal and external
              communication and optimize business processes.
            </p>
          </div>

          <div className="transport-logistics-factors-layout">
            {competitiveFactors.map((factor, index) => {
              const Icon = factor.icon;
              return (
                <div
                  key={index}
                  className="transport-logistics-factor-item transport-logistics-slide-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="transport-logistics-factor-item-top">
                    <div className="transport-logistics-factor-item-icon">
                      <Icon className="transport-logistics-medium-icon" />
                    </div>
                    <h3 className="transport-logistics-factor-item-title">
                      {factor.title}
                    </h3>
                  </div>
                  <div className="transport-logistics-factor-item-body">
                    <p className="transport-logistics-factor-item-text">
                      {factor.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Mobile Integration Section */}
      <section
        id="transport-logistics-mobile"
        className={`transport-logistics-mobile-area ${
          visibleSections.has("transport-logistics-mobile")
            ? "transport-logistics-section-visible"
            : ""
        }`}
      >
        <div className="transport-logistics-content-width">
          <div className="transport-logistics-mobile-layout">
            <div className="transport-logistics-mobile-image">
              <img
                src={MobileLogistics || "/placeholder.svg"}
                alt="Mobile application for logistics showing barcode scanning and goods receiving interface"
                className="transport-logistics-main-image transport-logistics-float-up"
              />
            </div>
            <div className="transport-logistics-mobile-content">
              <span className="transport-logistics-mobile-badge transport-logistics-slide-down">
                Mobile Integration
              </span>
              <h2 className="transport-logistics-mobile-heading transport-logistics-slide-right">
                Mobile Application for Goods Receiving
              </h2>
              <p className="transport-logistics-mobile-description transport-logistics-fade-in-up">
                Once the goods have moved to the destination, the item must
                receive the goods through the Mobile application and the item
                status is from In Transit Code at the receiving point.
              </p>
              <p className="transport-logistics-mobile-description transport-logistics-fade-in-up">
                Through mobile application, the receiving point has the
                opportunity to check and scan the incoming items and confirm the
                quantities with mobile.
              </p>
              <div className="transport-logistics-mobile-highlights">
                {[
                  "Real-time status updates",
                  "Barcode scanning",
                  "Quantity confirmation",
                ].map((highlight, index) => (
                  <div
                    key={index}
                    className="transport-logistics-mobile-highlight transport-logistics-slide-right"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <CheckCircle className="transport-logistics-check-icon" />
                    <span>{highlight}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NaviTrans Section */}
      <section
        id="transport-logistics-navitrans"
        className={`transport-logistics-navitrans-area ${
          visibleSections.has("transport-logistics-navitrans")
            ? "transport-logistics-section-visible"
            : ""
        }`}
      >
        <div className="transport-logistics-content-width">
          <div className="transport-logistics-center-align">
            <h2 className="transport-logistics-navitrans-heading transport-logistics-zoom-in">
              NaviTrans Logistic Software
            </h2>
            <p className="transport-logistics-navitrans-description transport-logistics-fade-in">
              Today, NaviTrans has become the most complete out-of-the-box
              logistic software in the market, with extensive and easy-to-use
              functions for road transportation, sea- and air- freight,
              intermodal transport and public warehousing.
            </p>
            <p className="transport-logistics-navitrans-description transport-logistics-fade-in">
              It helps you connect your business, take smart decisions, deliver
              your customers the service they expect and grow without worries.
              NaviTrans is available on-premise or as a hosted solution and is
              delivered in over 30 countries through our own offices and through
              an international network of certified NaviTrans partners.
            </p>
          </div>

          <div className="transport-logistics-transport-modes-layout">
            {transportModes.map((mode, index) => {
              const Icon = mode.icon;
              return (
                <div
                  key={index}
                  className="transport-logistics-transport-mode-item transport-logistics-slide-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="transport-logistics-transport-mode-icon">
                    <Icon className="transport-logistics-large-icon" />
                  </div>
                  <h3 className="transport-logistics-transport-mode-title">
                    {mode.title}
                  </h3>
                  <p className="transport-logistics-transport-mode-description">
                    {mode.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section
        id="transport-logistics-benefits"
        className={`transport-logistics-benefits-area ${
          visibleSections.has("transport-logistics-benefits")
            ? "transport-logistics-section-visible"
            : ""
        }`}
      >
        <div className="transport-logistics-content-width">
          <div className="transport-logistics-center-align">
            <h2 className="transport-logistics-benefits-heading transport-logistics-zoom-in">
              Benefits
            </h2>
            <p className="transport-logistics-benefits-description transport-logistics-fade-in">
              Transform your logistics operations with comprehensive benefits
              and proven results.
            </p>
          </div>

          <div className="transport-logistics-benefits-layout">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="transport-logistics-benefit-item transport-logistics-slide-up"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <CheckCircle className="transport-logistics-benefit-check-icon" />
                <span className="transport-logistics-benefit-text">
                  {benefit}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        id="transport-logistics-cta"
        className={`transport-logistics-cta-area ${
          visibleSections.has("transport-logistics-cta")
            ? "transport-logistics-section-visible"
            : ""
        }`}
      >
        <div className="transport-logistics-content-width transport-logistics-center-align">
          <h2 className="transport-logistics-cta-heading transport-logistics-zoom-in">
            Ready to Optimize Your Logistics?
          </h2>
          <p className="transport-logistics-cta-description transport-logistics-fade-in">
            Let our logistics experts design a comprehensive solution that
            streamlines your transport and logistics operations with Microsoft
            Dynamics NAV and NaviTrans.
          </p>
          <div className="transport-logistics-cta-buttons transport-logistics-bounce-in">
            <button
              className="transport-logistics-primary-button"
              onClick={() => setShowContact(true)}
            >
              <Users className="transport-logistics-small-icon" />
              Schedule Consultation
            </button>
          </div>
        </div>
      </section>

      {/* Contact Form Modal */}
      {showContact && (
        <div
          className="modal-overlay transport-logistics-fade-in"
          onClick={() => setShowContact(false)}
        >
          <div
            className="modal-content transport-logistics-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="modal-close"
              onClick={() => setShowContact(false)}
            >
              ✕
            </button>
            <ContactForm />
          </div>
        </div>
      )}
    </div>
  );
};

export default TransportLogistics;
